import { config } from '../config';
import { buildReasonHeader } from '../pages/approvalRequests.logic';
import { getAdminApiAuthHeaders } from './auth';

/**
 * DM-6437: in-console dual approval for the FM-105 destructive operations.
 *
 * The console used to call publish/delete directly and got 403 MISSING_REASON
 * back, because those operations need an X-Admin-Reason header plus an
 * X-Admin-Approval-Id token minted by a *different* operator. An approval
 * request moves that whole exchange into the console: the requester opens
 * one, a second operator decides it on the Approvals page, and on approval
 * the server mints the token and hands it back to the requester only. The
 * requester's page then replays the original call with both headers.
 */

export type DestructiveOperation =
  | 'publishContent'
  | 'publishContentRevision'
  | 'deleteContent'
  | 'activateExperiment'
  | 'archiveExperiment'
  | 'deleteCdnFile';

/**
 * DM-7214: what a publishContentRevision request points at. The edit lives on
 * the server as the item's proposed revision, so everything the console needs
 * to show the diff and to publish comes from here, never from page state.
 */
export interface ApprovalRequestRevisionRef {
  content_id: string;
  revision_id: string;
  hash: string;
  base_version: number;
}

export type ApprovalRequestStatus =
  'PENDING' | 'APPROVED' | 'REJECTED' | 'WITHDRAWN' | 'COMPLETED' | 'EXPIRED';

export type ApprovalRequestDecision = 'approved' | 'rejected';

export interface ApprovalRequest {
  id: string;
  operation: DestructiveOperation | string;
  target_id: string;
  target_label?: string;
  reason: string;
  note?: string;
  status: ApprovalRequestStatus;
  requester_id: string;
  requested_at: string;
  expires_at: string;
  decider_id?: string;
  decided_at?: string;
  decision_note?: string;
  /** Present only for the requester, and only while the request is APPROVED. */
  approval_token?: string;
  approval_expires_at?: string;
  completed_at?: string;
  /** DM-7214: present on publishContentRevision requests. */
  revision?: ApprovalRequestRevisionRef;
}

export interface CreateApprovalRequestInput {
  /** A revision's request is opened by proposing the revision, never from here. */
  operation: Exclude<DestructiveOperation, 'publishContentRevision'>;
  target_id: string;
  target_label?: string;
  reason: string;
  note?: string;
}

/** What a destructive call needs once a second operator has approved it. */
export interface Approval {
  reason: string;
  note?: string;
  approvalId: string;
}

export const ADMIN_REASON_HEADER = 'X-Admin-Reason';
export const ADMIN_APPROVAL_HEADER = 'X-Admin-Approval-Id';

/** The two FM-105 headers every destructive admin call must carry. */
export function approvalHeaders(approval: Approval): Record<string, string> {
  return {
    [ADMIN_REASON_HEADER]: buildReasonHeader(approval.reason, approval.note),
    [ADMIN_APPROVAL_HEADER]: approval.approvalId,
  };
}

interface ApiEnvelope<T> {
  data?: T;
}

function unwrap<T>(body: T | ApiEnvelope<T>): T {
  return (body as ApiEnvelope<T>).data ?? (body as T);
}

/**
 * Admin error bodies are RFC 7807 problems; `detail` is the user-facing copy
 * and `details.code` (SELF_APPROVAL, MISSING_APPROVAL, ...) the only field a
 * caller may branch on, so both survive into the thrown error.
 */
export class AdminApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string
  ) {
    super(message);
    this.name = 'AdminApiError';
  }
}

export async function readAdminError(res: Response, fallback: string): Promise<AdminApiError> {
  try {
    const body = await res.json();
    const message = body?.detail || body?.message || body?.data?.detail || fallback;
    const code = body?.details?.code || body?.code;
    return new AdminApiError(message, res.status, typeof code === 'string' ? code : undefined);
  } catch {
    return new AdminApiError(fallback, res.status);
  }
}

const BASE = () => `${config.api.url}/admin/approval-requests`;

export const approvalRequestsApi = {
  async create(input: CreateApprovalRequestInput): Promise<ApprovalRequest> {
    const headers = await getAdminApiAuthHeaders();
    const res = await fetch(BASE(), { method: 'POST', headers, body: JSON.stringify(input) });
    if (!res.ok) throw await readAdminError(res, 'Failed to request approval');
    return unwrap<ApprovalRequest>(await res.json());
  },

  async list(status?: ApprovalRequestStatus): Promise<ApprovalRequest[]> {
    const headers = await getAdminApiAuthHeaders();
    const url = new URL(BASE());
    if (status) url.searchParams.set('status', status);
    const res = await fetch(url.toString(), { headers });
    if (!res.ok) throw await readAdminError(res, 'Failed to list approval requests');
    const data = unwrap<{ items?: ApprovalRequest[] }>(await res.json());
    return data.items ?? [];
  },

  async get(id: string): Promise<ApprovalRequest> {
    const headers = await getAdminApiAuthHeaders();
    const res = await fetch(`${BASE()}/${encodeURIComponent(id)}`, { headers });
    if (!res.ok) throw await readAdminError(res, 'Failed to load approval request');
    return unwrap<ApprovalRequest>(await res.json());
  },

  async decide(
    id: string,
    decision: ApprovalRequestDecision,
    note?: string
  ): Promise<ApprovalRequest> {
    const headers = await getAdminApiAuthHeaders();
    const res = await fetch(`${BASE()}/${encodeURIComponent(id)}/decision`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ decision, note }),
    });
    if (!res.ok) throw await readAdminError(res, 'Failed to record the decision');
    return unwrap<ApprovalRequest>(await res.json());
  },

  async withdraw(id: string): Promise<ApprovalRequest> {
    const headers = await getAdminApiAuthHeaders();
    const res = await fetch(`${BASE()}/${encodeURIComponent(id)}/withdraw`, {
      method: 'POST',
      headers,
    });
    if (!res.ok) throw await readAdminError(res, 'Failed to withdraw the request');
    return unwrap<ApprovalRequest>(await res.json());
  },
};
